import { ONE_HUNDRED_PERCENT, SERVICE_ITEM_CATEGORIES, SPLIT_METHODS } from '@wodi/domain';
import { z } from 'zod';

import { ACTIVE_STATUSES } from './catalogue.ts';
import { actorSchema, isoDaySchema, reasonSchema, RECORD_STATUSES } from './clinical-common.ts';
import { amountSchema } from './money.ts';
import { PAYER_KINDS } from './visits.ts';

// Payers, their schemes, and who pays what (docs/plans/phase1-finance-catalogue.md
// §2.7, §2.8, §2.10). No SHA scheme code, tariff or split is defined here: SHA schemes
// come from eligibility responses and SHA split rules are TODO(verify-spec) (decisions 5, 6).

export { SPLIT_METHODS };

export const payerSchema = z.object({
  id: z.uuid(),
  kind: z.enum(PAYER_KINDS),
  name: z.string(),
  /** The built-in cash and SHA payers: can't be deactivated or change kind. */
  isSystem: z.boolean(),
  status: z.enum(ACTIVE_STATUSES),
  statusReason: z.string().nullable(),
  version: z.number().int(),
});

export type PayerView = z.infer<typeof payerSchema>;

export const SCHEME_ORIGINS = ['configured', 'eligibility'] as const;
export const SCHEME_REVIEW_STATES = ['reviewed', 'needs_review'] as const;

export const schemeSchema = z.object({
  id: z.uuid(),
  payerId: z.uuid(),
  name: z.string(),
  /** For SHA, the scheme code eligibility returned. TODO(verify-spec): field and values. */
  externalCode: z.string().nullable(),
  origin: z.enum(SCHEME_ORIGINS),
  reviewState: z.enum(SCHEME_REVIEW_STATES),
  status: z.enum(ACTIVE_STATUSES),
  statusReason: z.string().nullable(),
  version: z.number().int(),
});

export type SchemeView = z.infer<typeof schemeSchema>;

export const payerDetailSchema = payerSchema.extend({ schemes: z.array(schemeSchema) });

/** Cash and SHA exist already in every tenant; these kinds are refused here. */
export const createPayerRequestSchema = z.object({
  kind: z.enum(['insurer', 'corporate', 'other']),
  name: z.string().trim().min(2).max(120),
});

export const updatePayerRequestSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: z.string().trim().min(2).max(120).optional(),
    status: z.enum(ACTIVE_STATUSES).optional(),
    statusReason: reasonSchema.optional(),
  })
  .refine((v) => v.status !== 'inactive' || v.statusReason !== undefined, {
    message: 'Say why the payer is no longer used',
    path: ['statusReason'],
  });

export const createSchemeRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  externalCode: z.string().trim().min(1).max(64).nullable().default(null),
});

export const updateSchemeRequestSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: z.string().trim().min(2).max(120).optional(),
    status: z.enum(ACTIVE_STATUSES).optional(),
    statusReason: reasonSchema.optional(),
  })
  .refine((v) => v.status !== 'inactive' || v.statusReason !== undefined, {
    message: 'Say why the scheme is no longer used',
    path: ['statusReason'],
  });

export const reviewSchemeRequestSchema = z.object({ expectedVersion: z.number().int().min(1) });

// --- Split rules ---------------------------------------------------------------------

/** The terms of a rule, with exactly the parameters its method uses. */
export const splitTermsSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('payer_pays_all') }),
  z.object({ method: z.literal('patient_pays_all') }),
  z.object({
    method: z.literal('patient_copay_percent'),
    /** Basis points: 2000 is 20%. */
    copayBasisPoints: z.number().int().min(0).max(ONE_HUNDRED_PERCENT),
  }),
  z.object({ method: z.literal('patient_copay_fixed_per_unit'), amountPerUnit: amountSchema }),
  z.object({ method: z.literal('payer_limit_per_unit'), amountPerUnit: amountSchema }),
]);

export type SplitTermsView = z.infer<typeof splitTermsSchema>;

export const splitRuleSchema = z.object({
  id: z.uuid(),
  payerId: z.uuid(),
  schemeId: z.uuid().nullable(),
  category: z.enum(SERVICE_ITEM_CATEGORIES).nullable(),
  serviceItemId: z.uuid().nullable(),
  terms: splitTermsSchema,
  effectiveFrom: z.iso.datetime(),
  effectiveTo: z.iso.datetime().nullable(),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  createdBy: actorSchema,
});

export type SplitRuleView = z.infer<typeof splitRuleSchema>;

export const changeSplitRuleRequestSchema = z
  .object({
    schemeId: z.uuid().nullable(),
    category: z.enum(SERVICE_ITEM_CATEGORIES).nullable(),
    serviceItemId: z.uuid().nullable(),
    terms: splitTermsSchema,
    startsOn: z.union([z.literal('now'), isoDaySchema]),
    /** The rule the person was looking at for this scope; null when there was none. */
    expectedCurrentRuleId: z.uuid().nullable(),
  })
  .refine((v) => v.category === null || v.serviceItemId === null, {
    message: 'A rule applies to a category or to one item, not both',
    path: ['serviceItemId'],
  });

export const splitRuleListQuerySchema = z.object({
  includeHistory: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});
